import React, { useCallback, FC } from 'react'
import { TagNavigationItemProps } from '@sport1/types/web'
import HorizontalScrollView from '@sport1/news-styleguide/HorizontalScrollView'
import NonFlexingContainer from '@sport1/news-styleguide/NonFlexingContainer'
import { useRouter } from 'next/router'
import { RenderListItemProps } from '@sport1/news-styleguide/Types'
import { createTabBarRuler, renderTab } from '@/components/TabBar/utils'
import { FlatListRefType } from '@/components/HorizontalSlider/types'
import { RefElementType } from '@/components/TabBar/TabbarTypes'
import { TabbarConfig } from '@/components/TabBar/TabbarConfig'

type LivetickerTabBarProps = {
    tabs: TagNavigationItemProps[]
}

const keyExtractor = (item: TagNavigationItemProps) => `liveticker-tab-${item.title}`

const LivetickerTabBar: FC<LivetickerTabBarProps> = ({ tabs }) => {
    const router = useRouter()
    const containerRef = React.useRef<RefElementType | null>(null)
    const flatListRef = React.useRef<FlatListRefType | null>(null)

    const getInitialSelectedTab = useCallback(() => {
        const currentPath = router.asPath
        const itemIndex = tabs.findIndex(item => currentPath === item.href)

        return itemIndex === -1 ? 0 : itemIndex
    }, [router.asPath, tabs])

    const [selectedTabIndex, setSelectedTabIndex] = React.useState(getInitialSelectedTab())

    const renderTabItem = useCallback(
        ({ item, index }: RenderListItemProps<TagNavigationItemProps>) => {
            return renderTab({
                onPress: () => setSelectedTabIndex(index),
                isActive: selectedTabIndex === index,
                tabBarItem: { title: item.title, href: item.href || '' },
            })
        },
        [selectedTabIndex]
    )

    const initialScroll = useCallback(() => {
        // Create Ruler
        const ruler = createTabBarRuler()
        containerRef.current?.appendChild(ruler)

        // Get cointaner width
        const containerWidth = containerRef.current?.getBoundingClientRect().width || 0

        let scrollOffset = 0
        for (let i = 0; i < tabs.length; i++) {
            const title = tabs[i].title

            if (title && TabbarConfig.TAB_WIDTH_WITHOUT_CONTENT) {
                ruler.innerHTML += title

                const contentWidth =
                    ruler.offsetWidth + (i + 1) * TabbarConfig.TAB_WIDTH_WITHOUT_CONTENT

                if (i === selectedTabIndex && contentWidth < containerWidth) {
                    scrollOffset = 0
                    break
                } else if (i < tabs.length - 1) {
                    // if we need to scroll, we will always scroll to the last item because second half of liveticker tabs should currently always fit
                    scrollOffset = contentWidth
                }
            }
        }

        if (scrollOffset) {
            flatListRef.current?.scrollToOffset({ animated: true, offset: scrollOffset })
        }
    }, [selectedTabIndex, tabs])

    React.useEffect(() => {
        // we need to wait until all resources are loaded to calculate the correct scrollPosition
        window.addEventListener('load', initialScroll)
        return () => window.removeEventListener('load', initialScroll)
    }, [initialScroll])

    if (!tabs.length) {
        return null
    }

    return (
        <NonFlexingContainer
            innerRef={containerRef}
            position="relative"
            testID="liveticker-tab-bar"
        >
            <NonFlexingContainer zIndex={2}>
                <HorizontalScrollView
                    innerRef={flatListRef}
                    items={tabs}
                    renderItem={renderTabItem}
                    keyExtractor={keyExtractor}
                    backgroundColor="pearl"
                    spaceX={['none', 'none', 'standard']}
                    testID="123"
                />
            </NonFlexingContainer>
        </NonFlexingContainer>
    )
}

export default LivetickerTabBar
